import { useQuery } from "@tanstack/react-query";
import { getObliqFeePayer } from "@/lib/obliq";
import { GASLESS_DISPLAY_SOL } from "@/lib/fees";
import { fetchJupiterOrder } from "@/lib/jupiter";
import { deserializeVersionedTx, simulateSolanaFee } from "@/lib/solana";
import { lamportsToSol, tokenAmountToRaw } from "@/lib/utils";
import type { JupiterQuote, Token } from "@/types";
import type { FeeEstimate } from "./useSendFeeEstimate";

interface UseSwapFeeEstimateOptions {
	fromToken: Token | undefined;
	toToken: Token | undefined;
	fromAmount: number;
	quote: JupiterQuote | undefined;
	takerAddress: string | undefined;
	enabled?: boolean;
}

async function estimateSwapFee(
	fromToken: Token,
	toToken: Token,
	fromAmount: number,
	takerAddress: string,
): Promise<FeeEstimate> {
	const feePayer = await getObliqFeePayer();
	const rawAmount = tokenAmountToRaw(fromAmount, fromToken.decimals);
	const order = await fetchJupiterOrder(
		fromToken.address,
		toToken.address,
		rawAmount,
		takerAddress,
		feePayer,
	);
	const tx = deserializeVersionedTx(order.transaction);
	const result = await simulateSolanaFee(tx);

	return {
		feeSol: lamportsToSol(result.feeLamports),
		isGasless: true,
	};
}

export function useSwapFeeEstimate({
	fromToken,
	toToken,
	fromAmount,
	quote,
	takerAddress,
	enabled = true,
}: UseSwapFeeEstimateOptions) {
	const rawAmount =
		fromToken && fromAmount > 0 ? tokenAmountToRaw(fromAmount, fromToken.decimals) : 0n;

	const canEstimate =
		enabled &&
		Boolean(fromToken) &&
		Boolean(toToken) &&
		Boolean(quote) &&
		Boolean(takerAddress) &&
		rawAmount > 0n &&
		fromToken?.address !== toToken?.address;

	return useQuery<FeeEstimate>({
		queryKey: [
			"swapFee",
			takerAddress,
			fromToken?.address,
			toToken?.address,
			rawAmount.toString(),
			quote?.outAmount,
			quote?.contextSlot,
		],
		queryFn: () =>
			estimateSwapFee(fromToken!, toToken!, fromAmount, takerAddress!),
		enabled: canEstimate,
		staleTime: 30_000,
		gcTime: 60_000,
		retry: 1,
		placeholderData: { feeSol: GASLESS_DISPLAY_SOL, isGasless: true },
	});
}
